/**
 * Module-level cache + boot fetch for the desktop-shell flag (#216, #224).
 *
 * #224: `/desktop` was removed and the desktop shell is now AUTOMATIC for
 * desktop users — the auto-responsive `/` shell mounts the wide DesktopShell
 * when `desktop.shell_enabled` is true (now the server default, see
 * config.py). The flag is KEPT as a rollback lever: ops can set
 * DESKTOP_SHELL_ENABLED=false to pin every viewport back to mobile chrome.
 *
 * The flag rides the SAME `GET /api/v2/public/feature-flags` envelope as the
 * PWA flags — `main.tsx` does ONE boot fetch and projects both via
 * `parseDesktopShellEnabled` here. To avoid a mobile→desktop FLASH on every
 * load (the boot fetch resolves after first paint), the last value is
 * persisted to localStorage and read synchronously at init: a returning
 * desktop user reads `true` immediately and paints the desktop shell with no
 * flash; a first-ever visit flashes mobile once, then persists. Operator
 * toggles propagate on the next page load (no live re-fetch mid-session).
 */
export const DESKTOP_SHELL_FLAG_KEY = "desktop.shell_enabled" as const;
const DESKTOP_SHELL_STORAGE_KEY = "desktop.shell_enabled";

/** Pure projection: true ONLY when the raw flags map carries the key === true.
 *  Fed by the single boot fetch (main.tsx → fetchPublicFeatureFlagsRaw). */
export function parseDesktopShellEnabled(
  raw: Record<string, boolean> | null | undefined,
): boolean {
  return raw?.[DESKTOP_SHELL_FLAG_KEY] === true;
}

/** Seed the first-paint value from the persisted boot snapshot so a returning
 *  desktop user paints the shell without the mobile→desktop flash. Defaults to
 *  false (mobile) when absent / unavailable (SSR, private mode, first visit). */
function readPersistedDesktopShell(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DESKTOP_SHELL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistDesktopShell(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      DESKTOP_SHELL_STORAGE_KEY,
      enabled ? "true" : "false",
    );
  } catch {
    // quota / private-browsing restriction — ignore (mirrors persistSurfaceMode)
  }
}

let _desktopShellEnabled = readPersistedDesktopShell();
const _listeners = new Set<() => void>();

export function setDesktopShellEnabledCache(enabled: boolean): void {
  // Persist even when unchanged so a returning user's snapshot self-corrects
  // when the server flips the flag (e.g. ops rolls desktop back to mobile).
  persistDesktopShell(enabled);
  if (enabled === _desktopShellEnabled) return;
  _desktopShellEnabled = enabled;
  for (const listener of _listeners) listener();
}

/**
 * Apply the ONE boot feature-flags fetch to the desktop-shell cache.
 *
 * #232 follow-up (operator 17/07: "todo desktop mudou pra mobile e sumiu a
 * opção de trocar"): a `null`/`undefined` raw map means the boot fetch FAILED
 * or timed out (1500 ms) — NOT that ops disabled the shell. The old code piped
 * `setDesktopShellEnabledCache(parseDesktopShellEnabled(raw))` straight from
 * `raw`, so a transient failure persisted `false` and demoted a returning
 * desktop user to mobile — while `SurfaceModeToggle` returns null when
 * `!desktopEnabled`, stranding them with no way back. On failure we therefore
 * KEEP the last known-good snapshot untouched; only an authoritative map (a
 * successful fetch) flips the flag, so a real operator rollback still
 * self-corrects through that path.
 */
export function applyDesktopShellFromBootFetch(
  raw: Record<string, boolean> | null | undefined,
): void {
  if (raw == null) return; // fetch failed → preserve last known-good snapshot
  setDesktopShellEnabledCache(parseDesktopShellEnabled(raw));
}

/** Reads the boot snapshot (seeded from localStorage, then updated by the boot
 *  fetch via main.tsx). */
export function readDesktopShellEnabled(): boolean {
  return _desktopShellEnabled;
}

/**
 * Subscribe to flag changes — the `useSyncExternalStore` contract. Fires when
 * the boot fetch flips the cache, so `useResponsiveSurface` re-selects the
 * shell without waiting for an unrelated re-render. Returns an unsubscribe fn.
 */
export function subscribeDesktopShell(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Test-only: reset cache + listeners + persisted snapshot to the mobile
 *  default between tests. */
export function resetDesktopShellEnabledForTests(): void {
  _desktopShellEnabled = false;
  _listeners.clear();
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DESKTOP_SHELL_STORAGE_KEY);
  } catch {
    // ignore
  }
}
